import { Spinner } from '@/components/glitch-ui/spinner';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import {
  useReportsQuery,
  reportTasksQueryOptions,
  businessQueryOptions,
  reportTypesQueryOptions,
  reportRunsQueryOptions,
  useGenerateEngineReportMutation,
} from '@/lib/query-options';
import { FileText, BarChart3, TrendingUp, Target, Download, AlertCircle, Lightbulb, Check, Info, RefreshCw, X, Compass } from 'lucide-react';
import { DatePicker } from '@/components/ui/date-picker';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { usePostHog } from 'posthog-js/react';
import { Card, CardContent } from '@/components/ui/card';
import { TabCardPanel } from '@/components/glitch-ui/tab-card-panel';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { FilterDropdown } from '@/components/glitch-ui/filter-dropdown';
import { useAtomValue } from 'jotai';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import { organizationDataAtom, googleAdsAccountAtom } from '@/store';
import type { Report, ReportRun, ReportType, ReportTypeOption } from '@/lib/types';
import { ReportAddMenu, reportMenuItems, type LegacyReportOption } from '@/components/reports/report-add-menu';
import { refusalReason } from '@/components/reports/report-refusal';
import { runProgressLabel } from '@/lib/report-run-progress';
import { MarkdownContent } from '@/components/ui/markdown-content';
import { usePDFExport } from '@/hooks/usePDFExport';
import { useGenerateWeeklyReportMutation, useGenerateStrategicReportMutation, useGenerateIntelligenceReportMutation } from '@/lib/api-extensions/admin-queries';
import { withOrgReset } from '@/components/glitch-ui/with-org-reset';
import { SidebarContainer } from '@/components/glitch-ui/sidebar-container';
import { PageErrorState, PageLoadingState } from '@/components/glitch-ui/empty-state';
import { dismissFailedReportTask } from '@/lib/apis';
import { StrategyReportViewer } from '@/components/reports/strategy-report-viewer';
import { useStrategyReport } from '@/components/business/strategy-report-panel';
import { landingReportId } from '@/lib/reports-landing';

export const Route = createFileRoute('/_authenticated/_layout/reports/$reportId')({
  component: withOrgReset(ReportDetailPage),
  staticData: {
    title: 'Reports',
    icon: FileText,
  },
});

// Report type display configuration
const REPORT_TYPE_CONFIG: Record<ReportType, { label: string; icon: React.ElementType; color: string }> = {
  WEEKLY_PERFORMANCE: {
    label: 'Ad Performance Report',
    icon: BarChart3,
    color: 'text-blue-800',
  },
  MONTHLY_SUMMARY: {
    label: 'Ad Performance Report',
    icon: BarChart3,
    color: 'text-blue-800',
  },
  QUARTERLY_REVIEW: {
    label: 'Quarterly Review',
    icon: BarChart3,
    color: 'text-purple-800',
  },
  STRATEGIC_6MONTH: {
    label: 'Google Ads Report',
    icon: TrendingUp,
    color: 'text-orange-800',
  },
  COMPETITOR_REPORT: {
    label: 'Competitor Report',
    icon: Target,
    color: 'text-red-800',
  },
  INTELLIGENCE: {
    label: 'Intelligence Report',
    icon: Lightbulb,
    color: 'text-indigo-800',
  },
  CUSTOM_SHORT: {
    label: 'Ad Performance Report',
    icon: BarChart3,
    color: 'text-blue-800',
  },
  CUSTOM_LONG: {
    label: 'Ad Performance Report',
    icon: BarChart3,
    color: 'text-blue-800',
  },
  META_WEEKLY: {
    label: 'Meta Weekly Report',
    icon: BarChart3,
    color: 'text-blue-800',
  },
  META_MONTHLY: {
    label: 'Meta Monthly Report',
    icon: BarChart3,
    color: 'text-blue-800',
  },
  META_QUARTERLY: {
    label: 'Meta Quarterly Report',
    icon: BarChart3,
    color: 'text-purple-800',
  },
  STRATEGIC_PLAN: {
    label: 'Strategic Plan',
    icon: Compass,
    color: 'text-indigo-800',
  },
  META_AUDIT: {
    label: 'Meta Ads Audit',
    icon: BarChart3,
    color: 'text-purple-800',
  },
};

const REPORT_TYPE_FILTERS: { value: string; label: string; children?: { value: string; label: string }[] }[] = [
  // 'STRATEGY' (#6232) covers the living Strategy Report row, which is not a
  // dated `report` table row, and the Strategic Plan reports its skill writes
  // (#6799), which expandReportTypes turns it into.
  { value: 'STRATEGY', label: 'Strategic Plan' },
  {
    value: 'PERFORMANCE',
    label: 'Performance Reports',
    children: [
      { value: 'WEEKLY_PERFORMANCE', label: 'Weekly' },
      { value: 'MONTHLY_SUMMARY', label: 'Monthly' },
      { value: 'CUSTOM', label: 'Custom' },
    ],
  },
  { value: 'STRATEGIC_6MONTH', label: 'Google Ads Report' },
  { value: 'INTELLIGENCE', label: 'Intelligence Report' },
  { value: 'COMPETITOR_REPORT', label: 'Competitor Report' },
];

/** The FE-only strategy filter passes when no type filter is active or when
 * 'Strategy Report' is among the selected types. */
function strategyPassesFilter(selectedTypes: string[]): boolean {
  return selectedTypes.length === 0 || selectedTypes.includes('STRATEGY');
}

/** Expand filter values to report types: CUSTOM is both custom lengths, and STRATEGY the Strategic Plan reports. */
const FILTER_REPORT_TYPES: Record<string, string[]> = {
  CUSTOM: ['CUSTOM_SHORT', 'CUSTOM_LONG'],
  STRATEGY: ['STRATEGIC_PLAN'],
};

function expandReportTypes(types: string[]): string[] {
  return types.flatMap(t => FILTER_REPORT_TYPES[t] ?? [t]);
}

function buildRecentWeeks(count: number): Array<{ start: Date; end: Date }> {
  const today = new Date();
  today.setHours(12, 0, 0, 0); // Midday to avoid DST issues

  // Find last Monday
  const day = today.getDay();
  const daysToLastMonday = day === 0 ? 6 : day - 1;
  const lastMonday = new Date(today);
  lastMonday.setDate(today.getDate() - daysToLastMonday);

  // Skip current incomplete week — start from the previous completed Monday
  const prevMonday = new Date(lastMonday);
  prevMonday.setDate(lastMonday.getDate() - 7);

  return Array.from({ length: count }, (_, i) => {
    const start = new Date(prevMonday);
    start.setDate(prevMonday.getDate() - i * 7);
    start.setHours(12, 0, 0, 0);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(12, 0, 0, 0);

    return { start, end };
  });
}

function buildRecentMonths(count: number): Array<{ start: Date; end: Date; label: string; isPartial: boolean }> {
  const now = new Date();
  return Array.from({ length: count }, (_, i) => {
    const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const end = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
    const label = start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const isPartial = i === 0 && now.getDate() < end.getDate();
    return { start, end, label, isPartial };
  });
}

const RECENT_WEEKS = buildRecentWeeks(12);
const RECENT_MONTHS = buildRecentMonths(12);

function getReportTitle(report: Report): string {
  // For competitor reports, use the competitor name from metadata
  if (report.report_type === 'COMPETITOR_REPORT' && report.report_metadata?.competitor_name) {
    return `${report.report_metadata.competitor_name}`;
  }

  return REPORT_TYPE_CONFIG[report.report_type]?.label || 'Report';
}

function getReportPillStyle(reportType: ReportType): string {
  switch (reportType) {
    case 'WEEKLY_PERFORMANCE': return 'bg-blue-50 text-blue-700';
    case 'MONTHLY_SUMMARY': return 'bg-purple-50 text-purple-700';
    case 'CUSTOM_SHORT':
    case 'CUSTOM_LONG': return 'bg-orange-50 text-orange-700';
    default: return 'bg-gray-100 text-gray-600';
  }
}

function getReportPeriodPill(report: Report): string | null {
  switch (report.report_type) {
    case 'WEEKLY_PERFORMANCE': return 'Weekly';
    case 'MONTHLY_SUMMARY': return 'Monthly';
    case 'CUSTOM_SHORT':
    case 'CUSTOM_LONG': return 'Custom';
    default: return null;
  }
}

function getOrdinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

function formatOrdinalDate(date: Date): string {
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
}

function parseDateString(dateStr: string): Date {
  // Append T12:00:00 to avoid UTC midnight timezone shift
  return new Date(dateStr + 'T12:00:00');
}

function formatWeekdayOrdinal(date: Date): string {
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  return `${weekday} ${day}${getOrdinalSuffix(day)} ${month}`;
}

function getReportSubtitle(report: Report): string {
  switch (report.report_type) {
    case 'COMPETITOR_REPORT': {
      const url = report.report_metadata?.competitor_url as string | undefined;
      if (url) {
        return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
      }
      return formatOrdinalDate(new Date(report.created_at));
    }
    case 'INTELLIGENCE':
    case 'STRATEGIC_6MONTH':
      return formatOrdinalDate(new Date(report.created_at));
    case 'MONTHLY_SUMMARY': {
      const start = parseDateString(report.report_period_start);
      const end = parseDateString(report.report_period_end);
      return `${formatWeekdayOrdinal(start)} - ${formatWeekdayOrdinal(end)}`;
    }
    case 'WEEKLY_PERFORMANCE':
    case 'CUSTOM_SHORT':
    case 'CUSTOM_LONG': {
      const start = parseDateString(report.report_period_start);
      const end = parseDateString(report.report_period_end);
      return `${formatWeekdayOrdinal(start)} - ${formatWeekdayOrdinal(end)}`;
    }
    default: {
      const start = parseDateString(report.report_period_start);
      const end = parseDateString(report.report_period_end);
      return `${formatWeekdayOrdinal(start)} - ${formatWeekdayOrdinal(end)}`;
    }
  }
}

function ReportDetailPage() {
  const organizationData = useAtomValue(organizationDataAtom);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { reportId } = useParams({ from: '/_authenticated/_layout/reports/$reportId' });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const trackedReportRef = useRef<string | null>(null);
  const { exportToPDF, isExporting, progress, stage } = usePDFExport();
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [isPerformanceModalOpen, setIsPerformanceModalOpen] = useState(false);
  const [periodType, setPeriodType] = useState<'weekly' | 'monthly' | 'custom'>('weekly');
  const [selectedWeekIndex, setSelectedWeekIndex] = useState<number | null>(null);
  const [selectedMonthIndex, setSelectedMonthIndex] = useState<number | null>(null);
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>(undefined);
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>(undefined);
  const [includeExternalCampaigns, setIncludeExternalCampaigns] = useState(false);

  // Check if custom dates match a calendar month
  const customMatchesMonth = (() => {
    if (!customStartDate || !customEndDate) return null;
    const isFirstOfMonth = customStartDate.getDate() === 1;
    const lastOfMonth = new Date(customStartDate.getFullYear(), customStartDate.getMonth() + 1, 0);
    const isLastOfMonth = customEndDate.getDate() === lastOfMonth.getDate()
      && customEndDate.getMonth() === customStartDate.getMonth()
      && customEndDate.getFullYear() === customStartDate.getFullYear();
    if (isFirstOfMonth && isLastOfMonth) {
      return customStartDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    return null;
  })();

  const MAX_CUSTOM_RANGE_DAYS = 90;
  const customRangeDays = customStartDate && customEndDate
    ? Math.round((customEndDate.getTime() - customStartDate.getTime()) / (1000 * 60 * 60 * 24))
    : 0;
  const customRangeExceedsMax = customRangeDays > MAX_CUSTOM_RANGE_DAYS;

  const currentGoogleAdsAccount = useAtomValue(googleAdsAccountAtom);

  // Report generation mutations
  const generateWeeklyReportMutation = useGenerateWeeklyReportMutation();
  const generateStrategicReportMutation = useGenerateStrategicReportMutation();
  const generateIntelligenceReportMutation = useGenerateIntelligenceReportMutation();
  const generateEngineReportMutation = useGenerateEngineReportMutation();
  const organizationId = organizationData?.id ?? 0;
  const [engineOption, setEngineOption] = useState<ReportTypeOption | null>(null);
  const { data: reportTypes = [] } = useQuery(reportTypesQueryOptions(organizationId));
  const { data: activeRuns = [] } = useQuery(reportRunsQueryOptions(organizationId, true));
  const { data: recentRuns = [] } = useQuery(reportRunsQueryOptions(organizationId));

  // Fetch business data for the current organization (1:1 relationship)
  const { data: business } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });

  const { data: allReports = [], isLoading, isError, refetch } = useReportsQuery(
    organizationData?.id,
    currentPage,
    pageSize,
  );

  const expandedTypes = expandReportTypes(selectedTypes);
  const reports = selectedTypes.length > 0
    ? allReports.filter((r) => expandedTypes.includes(r.report_type))
    : allReports;

  // Check if selected period has an existing report
  const existingReportForPeriod = (() => {
    let startDate: Date | undefined;
    let endDate: Date | undefined;
    if (periodType === 'weekly' && selectedWeekIndex !== null) {
      startDate = RECENT_WEEKS[selectedWeekIndex].start;
      endDate = RECENT_WEEKS[selectedWeekIndex].end;
    } else if (periodType === 'monthly' && selectedMonthIndex !== null) {
      startDate = RECENT_MONTHS[selectedMonthIndex].start;
      endDate = RECENT_MONTHS[selectedMonthIndex].end;
    } else if (periodType === 'custom' && customStartDate && customEndDate) {
      startDate = customStartDate;
      endDate = customEndDate;
    }
    if (!startDate || !endDate) return null;
    const startStr = startDate.toLocaleDateString('sv-SE');
    const endStr = endDate.toLocaleDateString('sv-SE');
    const performanceTypes = ['WEEKLY_PERFORMANCE', 'MONTHLY_SUMMARY', 'CUSTOM_SHORT', 'CUSTOM_LONG'];
    return allReports.find(
      (r) => r.report_period_start === startStr && r.report_period_end === endStr && performanceTypes.includes(r.report_type)
    ) || null;
  })();

  // Poll task tracking status by business_id
  const { data: taskStatus } = useQuery({
    ...reportTasksQueryOptions(business?.id ?? 0),
    enabled: !!business?.id,
  });


  // Check if a report of the given type was already generated today
  const isGeneratedToday = (reportType: ReportType): boolean => {
    const today = new Date().toDateString();
    return reports.some(
      (r) => r.report_type === reportType && new Date(r.created_at).toDateString() === today
    );
  };

  const handleGenerateEngine = async (option: ReportTypeOption, startDate?: Date, endDate?: Date) => {
    if (!organizationData?.id) return;
    posthog?.capture('report_generation_started', {
      report_type: option.key,
      organization_id: organizationData.id,
      google_ads_account_id: currentGoogleAdsAccount?.id,
    });
    try {
      await generateEngineReportMutation.mutateAsync({
        organizationId: organizationData.id,
        request: {
          definition_key: option.key,
          ...(option.needs_google_ads_account && currentGoogleAdsAccount
            ? { googleadsaccount_id: parseInt(currentGoogleAdsAccount.id, 10) }
            : {}),
          ...(option.needs_business && business?.id ? { business_id: business.id } : {}),
          ...(startDate && endDate
            ? { period_start: startDate.toLocaleDateString('sv-SE'), period_end: endDate.toLocaleDateString('sv-SE') }
            : {}),
        },
      });
      setIsPerformanceModalOpen(false);
      setEngineOption(null);
    } catch (error) {
      posthog?.capture('report_generation_failed', {
        report_type: option.key,
        organization_id: organizationData.id,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      toast({
        title: 'Report Generation Failed',
        description: refusalReason(error),
        variant: 'destructive',
      });
    }
  };

  const handleSelectReportType = (option: ReportTypeOption) => {
    if (option.needs_period) {
      setEngineOption(option);
      setIsPerformanceModalOpen(true);
      return;
    }
    void handleGenerateEngine(option);
  };

  const handleGenerateWeekly = async () => {
    if (!organizationData?.id) return;
    let startDate: Date | undefined;
    let endDate: Date | undefined;
    if (periodType === 'weekly' && selectedWeekIndex !== null) {
      const week = RECENT_WEEKS[selectedWeekIndex];
      startDate = week.start;
      endDate = week.end;
    } else if (periodType === 'monthly' && selectedMonthIndex !== null) {
      const month = RECENT_MONTHS[selectedMonthIndex];
      startDate = month.start;
      endDate = month.end;
    } else if (periodType === 'custom') {
      startDate = customStartDate;
      endDate = customEndDate;
    }
    if (engineOption) {
      await handleGenerateEngine(engineOption, startDate, endDate);
      return;
    }

    posthog?.capture('report_generation_started', {
      report_type: 'WEEKLY_PERFORMANCE',
      organization_id: organizationData.id,
      google_ads_account_id: currentGoogleAdsAccount?.id,
    });
    try {
      await generateWeeklyReportMutation.mutateAsync({
        organizationId: organizationData.id,
        reportWeekStart: startDate,
        reportWeekEnd: endDate,
        includeExternal: includeExternalCampaigns,
      });
      setIsPerformanceModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['report-tasks', business?.id] });
    } catch (error) {
      posthog?.capture('report_generation_failed', {
        report_type: 'WEEKLY_PERFORMANCE',
        organization_id: organizationData.id,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      toast({
        title: 'Report Generation Failed',
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
        variant: 'destructive',
      });
    }
  };

  const handleGenerateStrategic = async () => {
    if (!organizationData?.id || !business?.id || !currentGoogleAdsAccount) return;
    posthog?.capture('report_generation_started', {
      report_type: 'STRATEGIC_6MONTH',
      organization_id: organizationData.id,
      google_ads_account_id: currentGoogleAdsAccount.id,
    });
    try {
      await generateStrategicReportMutation.mutateAsync({
        organizationId: organizationData.id,
        googleadsaccountId: parseInt(currentGoogleAdsAccount.id, 10),
        businessId: business.id,
      });
      queryClient.invalidateQueries({ queryKey: ['report-tasks', business.id] });
    } catch (error) {
      posthog?.capture('report_generation_failed', {
        report_type: 'STRATEGIC_6MONTH',
        organization_id: organizationData.id,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      toast({
        title: 'Report Generation Failed',
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
        variant: 'destructive',
      });
    }
  };

  const handleGenerateIntelligence = async () => {
    if (!organizationData?.id || !business?.id) return;
    posthog?.capture('report_generation_started', {
      report_type: 'INTELLIGENCE',
      organization_id: organizationData.id,
      google_ads_account_id: currentGoogleAdsAccount?.id,
    });
    try {
      await generateIntelligenceReportMutation.mutateAsync({
        organizationId: organizationData.id,
        googleadsaccountId: currentGoogleAdsAccount ? parseInt(currentGoogleAdsAccount.id, 10) : 0,
        businessId: business.id,
      });
      queryClient.invalidateQueries({ queryKey: ['report-tasks', business.id] });
    } catch (error) {
      posthog?.capture('report_generation_failed', {
        report_type: 'INTELLIGENCE',
        organization_id: organizationData.id,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      toast({
        title: 'Report Generation Failed',
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
        variant: 'destructive',
      });
    }
  };

  const legacyReportOptions: LegacyReportOption[] = [
    {
      key: 'legacy-performance',
      label: 'Ad Performance Report',
      icon: BarChart3,
      iconColor: 'text-blue-800',
      reportTypes: ['WEEKLY_PERFORMANCE', 'MONTHLY_SUMMARY', 'CUSTOM_SHORT', 'CUSTOM_LONG'],
      disabled: !organizationData?.id || !currentGoogleAdsAccount,
      pending: false,
      done: isGeneratedToday('WEEKLY_PERFORMANCE'),
      onSelect: () => {
        setEngineOption(null);
        setIsPerformanceModalOpen(true);
      },
    },
    {
      key: 'legacy-google-ads',
      label: 'Google Ads Report',
      icon: TrendingUp,
      iconColor: 'text-orange-800',
      reportTypes: ['STRATEGIC_6MONTH'],
      disabled:
        isGeneratedToday('STRATEGIC_6MONTH') ||
        generateStrategicReportMutation.isPending ||
        !organizationData?.id ||
        !business?.id ||
        !currentGoogleAdsAccount,
      pending: generateStrategicReportMutation.isPending,
      done: isGeneratedToday('STRATEGIC_6MONTH'),
      onSelect: handleGenerateStrategic,
    },
    {
      key: 'legacy-intelligence',
      label: 'Intelligence Report',
      icon: Lightbulb,
      iconColor: 'text-indigo-800',
      reportTypes: ['INTELLIGENCE'],
      disabled:
        isGeneratedToday('INTELLIGENCE') ||
        generateIntelligenceReportMutation.isPending ||
        !organizationData?.id ||
        !business?.id,
      pending: generateIntelligenceReportMutation.isPending,
      done: isGeneratedToday('INTELLIGENCE'),
      onSelect: handleGenerateIntelligence,
    },
  ];
  const requestableTypes = reportTypes.map((option): ReportTypeOption =>
    option.available && option.needs_google_ads_account && !currentGoogleAdsAccount
      ? { ...option, available: false, reason: 'Select a Google Ads account first' }
      : option.available && option.needs_business && !business?.id
        ? { ...option, available: false, reason: 'Set up the business profile first' }
        : option,
  );
  const menuItems = reportMenuItems(
    requestableTypes,
    legacyReportOptions,
    handleSelectReportType,
    generateEngineReportMutation.isPending ? generateEngineReportMutation.variables?.request.definition_key ?? null : null,
  );

  const mirroredTaskIds = new Set(
    [...activeRuns, ...recentRuns].map((run) => run.task_tracking_id).filter((id): id is number => id !== null),
  );
  const runsByTaskId = new Map<number, ReportRun>(
    recentRuns.filter((run) => run.task_tracking_id !== null).map((run) => [run.task_tracking_id as number, run]),
  );
  const [prevActiveRunCount, setPrevActiveRunCount] = useState<number | null>(null);
  useEffect(() => {
    if (prevActiveRunCount !== null && activeRuns.length < prevActiveRunCount) {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      queryClient.invalidateQueries({ queryKey: ['report-runs', organizationId] });
      queryClient.invalidateQueries({ queryKey: ['report-tasks', business?.id] });
    }
    setPrevActiveRunCount(activeRuns.length);
  }, [activeRuns.length, prevActiveRunCount, queryClient, organizationId, business?.id]);

  // Track previous has_in_progress to detect when all tasks complete
  const [prevHasInProgress, setPrevHasInProgress] = useState<boolean | null>(null);

  // When all tasks complete (has_in_progress goes from true to false), refetch reports
  useEffect(() => {
    if (taskStatus) {
      const currentHasInProgress = taskStatus.has_in_progress;

      // If we were polling and now there are no in-progress tasks, refetch reports
      if (prevHasInProgress === true && currentHasInProgress === false) {
        queryClient.invalidateQueries({ queryKey: ['reports'] });
        // Invalidate notifications so any new notifications appear
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notificationCounts'] });
      }

      setPrevHasInProgress(currentHasInProgress);
    }
  }, [taskStatus?.has_in_progress, queryClient, prevHasInProgress]);

  const selectedReport = reports.find((r) => r.id === Number(reportId)) || null;

  // The one living Strategy Report (#6232) renders inline as the special
  // 'strategy' id, keeping the reports sidebar in place.
  const isStrategySelected = reportId === 'strategy';
  const { report: strategyReport, inFlight: strategyInFlight } = useStrategyReport(
    organizationData?.id ?? 0,
    business?.id ?? 0,
  );
  const showStrategyRow = (strategyReport !== null || strategyInFlight !== null) && strategyPassesFilter(selectedTypes);
  // The strategy viewer portals its toolbar buttons into the tab bar's
  // actions slot so they sit outside the card, like Export does for dated
  // reports.
  const [strategyActionsEl, setStrategyActionsEl] = useState<HTMLElement | null>(null);

  // Track report viewed - only once per report
  useEffect(() => {
    if (selectedReport && trackedReportRef.current !== reportId) {
      trackedReportRef.current = reportId;
      posthog?.capture('report_viewed', {
        report_id: selectedReport.id,
        report_type: selectedReport.report_type,
        organization_id: organizationData?.id,
      });
    }
  }, [selectedReport, reportId, posthog, organizationData?.id]);

  if (isLoading) {
    return <PageLoadingState />;
  }

  if (isError) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between py-0.5">
          <div className="flex items-center gap-3"></div>
        </div>
        <div className="flex-1">
          <PageErrorState
            title="Couldn't Load Reports"
            description="This page failed to load. This is a loading error, not missing data, so nothing has been lost. Retry, or contact support if it persists."
            onRetry={() => refetch()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* PDF Export Loading Modal */}
      {isExporting && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="p-6 w-80">
            <CardContent className="p-0">
              <div className="text-center">
                <Spinner size="xl" className="mx-auto mb-4 text-[#290BD9]" />
                <p className="text-gray-900 font-medium mb-2">{stage || 'Preparing export...'}</p>
                <Progress value={progress} className="h-2" />
                <p className="text-sm text-gray-500 mt-2">{Math.round(progress)}% complete</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Show full-width empty state when no reports exist (a living strategy
          report counts as a report — keep the sidebar layout for it). The same
          rule the index lands by (#6707), so 'none' here means the list would
          truly be empty. */}
      {reportId === 'none' &&
      landingReportId({
        reportIds: reports.map((r) => r.id),
        hasInProgress: !!taskStatus?.has_in_progress || activeRuns.length > 0,
        hasStrategyRow: strategyReport !== null || strategyInFlight !== null,
      }) === 'none' ? (
        <>
          {/* Empty header */}
          <div className="flex items-center justify-between py-0.5">
            <div className="flex items-center gap-3"></div>
          </div>
          <div className="space-y-6">
            <TabCardPanel
              tabs={[
                { id: 'all', label: 'All Reports', isActive: true },
                ...(business?.brand_identity ? [{
                  id: 'new',
                  label: '+ Add',
                  render: () => (
                    <ReportAddMenu items={menuItems} />
                  ),
                }] : []),
              ]}
            >
              <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
                <FileText className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
                <h3 className="text-lg font-semibold text-gray-500 mb-2">No Reports Found</h3>
                <p className="text-gray-500">Generate your first report using the Add tab above</p>
              </div>
            </TabCardPanel>
          </div>
        </>
      ) : (
      <>
      {/* Header - matches business page pattern */}
      <div className="flex items-center justify-between py-0.5">
        <div className="flex items-center gap-3"></div>
      </div>

      <div className="flex-1">
        {/* Main Layout: 12-Column Grid */}
        <div className="grid grid-cols-12 gap-1">
          {/* Left: Reports List (25%) */}
          <div className="col-span-3 sticky top-0 self-start">
            <TabCardPanel
              tabs={[
                { id: 'all', label: 'All Reports', isActive: true },
                ...(business?.brand_identity ? [{
                  id: 'new',
                  label: '+ Add',
                  render: () => (
                    <ReportAddMenu items={menuItems} />
                  ),
                }] : []),
              ]}
              actions={
                <FilterDropdown
                  sections={[
                    { id: 'types', title: 'Report Type', options: REPORT_TYPE_FILTERS.map(f => ({
                      value: f.value,
                      label: f.label,
                      children: f.children?.map(c => ({ value: c.value, label: c.label })),
                    }))},
                  ]}
                  selectedValues={{ types: selectedTypes }}
                  showDateRange={false}
                  popoverWidth="w-[280px]"
                  onApply={(filters) => {
                    setSelectedTypes(filters.selections.types || []);
                    setCurrentPage(1);
                  }}
                />
              }
              noPadding
            >
                <SidebarContainer
                  pagination={reports.length >= pageSize ? {
                    currentPage,
                    onPreviousPage: () => setCurrentPage((p) => Math.max(1, p - 1)),
                    onNextPage: () => setCurrentPage((p) => p + 1),
                    isPreviousDisabled: currentPage === 1,
                    isNextDisabled: reports.length < pageSize,
                  } : undefined}
                >
                {reports.length === 0 && !taskStatus?.has_in_progress && activeRuns.length === 0 && !showStrategyRow ? (
                  <div className="flex items-center justify-center p-6 text-gray-500">
                    <span>{selectedTypes.length > 0 ? 'No matching reports' : 'No reports found.'}</span>
                  </div>
                ) : (
                  <>
                    {/* The one living Strategy Report (#6232), an ordinary row
                        at the top of the stream — it is updated, not re-issued. */}
                    {showStrategyRow && (
                      <Card
                        className={`m-1 rounded-md border-0 shadow-none transition-colors cursor-pointer ${
                          isStrategySelected ? 'bg-[#290BD9]/10' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => navigate({ to: '/reports/$reportId', params: { reportId: 'strategy' } })}
                      >
                        <div className="relative p-2">
                          <div className="flex items-center gap-2">
                            <Compass className="h-4 w-4 text-[#290BD9] flex-shrink-0" />
                            <div className="text-base font-medium text-gray-900">Strategic Plan</div>
                          </div>
                          <div className="pl-6 text-sm font-normal text-gray-500">
                            {strategyInFlight ? (
                              <span className="flex items-center gap-2">
                                <Spinner size="sm" /> Generating...
                              </span>
                            ) : (
                              formatOrdinalDate(
                                  new Date(
                                    strategyReport!.markdown_updated_at ??
                                      strategyReport!.finished_at ??
                                      strategyReport!.created_at,
                                  ),
                                )
                            )}
                          </div>
                        </div>
                      </Card>
                    )}
                    {activeRuns.map((run) => (
                      <Card key={`run-${run.id}`} className="m-1 rounded-md border-0 shadow-none">
                        <div className="relative p-2">
                          <div className="pr-4">
                            <div className="flex items-center gap-2">
                              <Spinner size="sm" className="text-gray-500 flex-shrink-0" />
                              <div className="text-base font-medium text-gray-900">Generating {run.label}</div>
                            </div>
                            <div className="text-sm font-normal text-gray-500 pl-6">
                              {runProgressLabel(run)} ·{' '}
                              Started {new Date(run.queued_at).toLocaleTimeString()}
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                    {/* Show in-progress reports at the top */}
                    {taskStatus?.has_in_progress && taskStatus.tasks
                      .filter((task) => task.state === 'IN_PROGRESS' && !mirroredTaskIds.has(task.task_tracking_id))
                      .map((task) => {
                        // Get label based on task type
                        const getTaskLabel = () => {
                          switch (task.task_type) {
                            case 'COMPETITOR_REPORT':
                              return 'Researching Competitor';
                            case 'STRATEGIC_REPORT_GENERATION':
                              return 'Generating Google Ads Report';
                            case 'WEEKLY_REPORT_GENERATION':
                              return 'Generating Ad Performance Report';
                            case 'INTELLIGENCE_REPORT_GENERATION':
                              return 'Generating Intelligence Report';
                            default:
                              return 'Generating Report';
                          }
                        };

                        return (
                          <Card
                            key={`generating-${task.task_tracking_id}`}
                            className="m-1 rounded-md border-0 shadow-none"
                          >
                            <div className="relative p-2">
                              <div className="pr-4">
                                <div className="flex items-center gap-2">
                                  <Spinner size="sm" className="text-gray-500 flex-shrink-0" />
                                  <div className="text-base font-medium text-gray-900">
                                    {getTaskLabel()}
                                  </div>
                                </div>
                                <div className="text-sm font-normal text-gray-500 pl-6">
                                  Started {task.created_at ? new Date(task.created_at).toLocaleTimeString() : 'recently'}
                                </div>
                              </div>
                            </div>
                          </Card>
                        );
                      })
                    }
                    {/* Show failed reports */}
                    {taskStatus?.tasks
                      .filter((task) => task.state === 'FAILED')
                      .map((task) => {
                        const engineRun = runsByTaskId.get(task.task_tracking_id);
                        // Get label based on task type
                        const getFailedTaskLabel = () => {
                          if (engineRun) return `${engineRun.label} Failed`;
                          switch (task.task_type) {
                            case 'COMPETITOR_REPORT':
                              return 'Competitor Report Failed';
                            case 'STRATEGIC_REPORT_GENERATION':
                              return 'Google Ads Report Failed';
                            case 'WEEKLY_REPORT_GENERATION':
                              return 'Ad Performance Report Failed';
                            case 'INTELLIGENCE_REPORT_GENERATION':
                              return 'Intelligence Report Failed';
                            default:
                              return 'Report Failed';
                          }
                        };

                        const engineRetryOption = engineRun
                          ? reportTypes.find((option) => option.key === engineRun.definition_key && option.available)
                          : undefined;
                        const handleRetry = () => {
                          if (engineRetryOption) {
                            handleSelectReportType(engineRetryOption);
                            return;
                          }
                          switch (task.task_type) {
                            case 'WEEKLY_REPORT_GENERATION':
                              setIsPerformanceModalOpen(true);
                              break;
                            case 'STRATEGIC_REPORT_GENERATION':
                              handleGenerateStrategic();
                              break;
                            case 'INTELLIGENCE_REPORT_GENERATION':
                              handleGenerateIntelligence();
                              break;
                            case 'COMPETITOR_REPORT':
                              window.location.href = '/competitors';
                              break;
                          }
                        };
                        const isCompetitorReport = task.task_type === 'COMPETITOR_REPORT';

                        return (
                          <Card
                            key={`failed-${task.task_tracking_id}`}
                            className="m-1 rounded-md border-0 shadow-none"
                          >
                            <div className="relative p-2 flex items-center">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                                  <div className="text-base font-medium text-gray-900">
                                    {getFailedTaskLabel()}
                                  </div>
                                </div>
                                <div className="text-sm font-normal text-gray-500 pl-6">
                                  {engineRun?.error_message
                                    ? `${engineRun.error_message} (${engineRun.error_code})`
                                    : task.error ? task.error : 'Generation failed'}
                                </div>
                              </div>
                              <div className="flex flex-col gap-0.5 shrink-0">
                                <button
                                  onClick={async () => {
                                    if (business?.id && task.task_tracking_id) {
                                      try {
                                        await dismissFailedReportTask(business.id, task.task_tracking_id);
                                        queryClient.invalidateQueries({ queryKey: ['report-tasks', business.id] });
                                      } catch {
                                        toast({ description: 'Failed to dismiss task. Please try again.', variant: 'destructive' });
                                      }
                                    }
                                  }}
                                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                  title="Dismiss"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={handleRetry}
                                  className="p-1.5 text-gray-400 hover:text-[#290BD9] hover:bg-[#290BD9]/10 rounded-md transition-colors"
                                  title={isCompetitorReport ? 'Go to Competitors' : 'Retry'}
                                >
                                  <RefreshCw className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          </Card>
                        );
                      })
                    }
                    {reports.map((report) => {
                      const config = REPORT_TYPE_CONFIG[report.report_type];
                      const ReportIcon = config.icon;
                      const isSelected = selectedReport?.id === report.id;

                      return (
                        <Card
                          key={report.id}
                          className={`m-1 rounded-md border-0 shadow-none transition-colors cursor-pointer ${
                            isSelected
                              ? 'bg-[#290BD9]/10'
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => navigate({ to: '/reports/$reportId', params: { reportId: String(report.id) } })}
                        >
                          <div className="relative p-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <ReportIcon className={`h-4 w-4 ${config.color} flex-shrink-0`} />
                                <div className="text-base font-medium text-gray-900">
                                  {getReportTitle(report)}
                                </div>
                              </div>
                              <div className="flex flex-wrap items-end justify-between gap-1 pl-6">
                                <div className="text-sm font-normal text-gray-500">
                                  {getReportSubtitle(report)}
                                  {report.updated_at && report.updated_at !== report.created_at && (
                                    <span className="text-xs text-gray-400 ml-1">· Updated {formatOrdinalDate(new Date(report.updated_at))}</span>
                                  )}
                                </div>
                                {getReportPeriodPill(report) && (
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getReportPillStyle(report.report_type)}`}>
                                    {getReportPeriodPill(report)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </>
                )}
                </SidebarContainer>
            </TabCardPanel>
          </div>

          {/* Right: Report Detail (75%) */}
          <div className="col-span-9">
            <TabCardPanel
              tabs={[{
                id: 'report',
                label: isStrategySelected ? 'Strategic Plan' : selectedReport ? getReportTitle(selectedReport) : 'Report',
                isActive: true,
              }]}
              actions={
                isStrategySelected ? <div ref={setStrategyActionsEl} className="flex items-center" /> : (
                <Button
                  data-testid="report-export-pdf"
                  variant="outline"
                  className="h-9 text-gray-900"
                  disabled={!selectedReport || isExporting}
                  onClick={async () => {
                    if (selectedReport) {
                      posthog?.capture('report_pdf_exported', {
                        report_id: selectedReport.id,
                        report_type: selectedReport.report_type,
                        organization_id: organizationData?.id,
                      });
                      await exportToPDF(selectedReport);
                    }
                  }}
                >
                  {isExporting ? (
                    <Spinner size="sm" className="mr-2" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  {isExporting ? 'Exporting...' : 'Export'}
                </Button>
                )
              }
            >
                {isStrategySelected ? (
                  strategyReport ? (
                    <StrategyReportViewer
                      orgId={organizationData?.id ?? 0}
                      report={strategyReport}
                      actionsContainer={strategyActionsEl}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      {strategyInFlight ? (
                        <>
                          <div className="w-12 h-12 rounded-full border-4 border-gray-200 border-t-[#290BD9] animate-spin mb-4"></div>
                          <h3 className="text-lg font-semibold text-gray-500 mb-2">Report Generating</h3>
                          <p className="text-gray-500">The Strategic Plan is being written. It appears here in a few minutes.</p>
                        </>
                      ) : (
                        <>
                          <Compass className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
                          <h3 className="text-lg font-semibold text-gray-500 mb-2">No Strategic Plan Yet</h3>
                          <p className="text-gray-500">Generate the plan from Business, Strategy tab.</p>
                        </>
                      )}
                    </div>
                  )
                ) : selectedReport ? (
                  <MarkdownContent content={selectedReport.content} />
                ) : reportId === 'none' ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <FileText className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
                    <h3 className="text-lg font-semibold text-gray-500 mb-2">No Reports Found</h3>
                    <p className="text-gray-500">Reports will appear here once generated</p>
                  </div>
                ) : reportId === 'generating' ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-12 h-12 rounded-full border-4 border-gray-200 border-t-[#290BD9] animate-spin mb-4"></div>
                    <h3 className="text-lg font-semibold text-gray-500 mb-2">Generating Report</h3>
                    <p className="text-gray-500">Your report is being generated. This may take a few minutes.</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <FileText className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
                    <h3 className="text-lg font-semibold text-gray-500 mb-2">Report Not Found</h3>
                    <p className="text-gray-500">The requested report could not be found</p>
                  </div>
                )}
            </TabCardPanel>
          </div>
        </div>
      </div>
      </>
      )}

      {/* Ad Performance Report Modal */}
      <Dialog
        open={isPerformanceModalOpen}
        onOpenChange={(open) => {
          setIsPerformanceModalOpen(open);
          if (!open) setEngineOption(null);
        }}
      >
        <DialogContent className="w-[420px]" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Generate {engineOption ? engineOption.label : 'Ad Performance Report'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Period type selector */}
            <div>
              <p className="text-sm text-gray-500 mb-2">Report period</p>
              <div className="flex gap-2">
                {(['weekly', 'monthly', 'custom'] as const).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setPeriodType(type)}
                    className={`flex-1 py-2 text-sm font-medium rounded-md border transition-colors ${
                      periodType === type
                        ? 'bg-[#290BD9] text-white border-[#290BD9]'
                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {type === 'weekly' ? 'Weekly' : type === 'monthly' ? 'Monthly' : 'Custom'}
                  </button>
                ))}
              </div>
            </div>

            {/* Weekly: scrollable list of recent weeks */}
            {periodType === 'weekly' && (
              <div>
              <p className="text-xs text-gray-500 mb-2">Only completed weeks are shown. For an incomplete week, use a custom report.</p>
              <div className="overflow-y-auto max-h-52 rounded-md border border-gray-200 divide-y divide-gray-100">
                {RECENT_WEEKS.map((week, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSelectedWeekIndex(i)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-sm transition-colors text-left ${
                      selectedWeekIndex === i
                        ? 'bg-[#290BD9]/10 text-[#290BD9] font-medium'
                        : 'hover:bg-gray-50 text-gray-900'
                    }`}
                  >
                    <span>{formatWeekdayOrdinal(week.start)} – {formatWeekdayOrdinal(week.end)}</span>
                    {selectedWeekIndex === i && <Check className="h-4 w-4 flex-shrink-0" />}
                  </button>
                ))}
              </div>
              </div>
            )}

            {/* Monthly: scrollable list of recent months */}
            {periodType === 'monthly' && (
              <div className="overflow-y-auto max-h-52 rounded-md border border-gray-200 divide-y divide-gray-100">
                {RECENT_MONTHS.map((month, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSelectedMonthIndex(i)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-sm transition-colors text-left ${
                      selectedMonthIndex === i
                        ? 'bg-[#290BD9]/10 text-[#290BD9] font-medium'
                        : 'hover:bg-gray-50 text-gray-900'
                    }`}
                  >
                    <span>
                      {month.label}
                      {month.isPartial && <span className="text-xs text-amber-600 ml-1.5">(partial)</span>}
                    </span>
                    {selectedMonthIndex === i && <Check className="h-4 w-4 flex-shrink-0" />}
                  </button>
                ))}
              </div>
            )}

            {/* Custom: start + end dates */}
            {periodType === 'custom' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1.5">Start date</p>
                    <DatePicker
                      date={customStartDate}
                      onSelect={setCustomStartDate}
                      placeholder="Start"
                      className="w-full h-9"
                      showClearButton={true}
                      disabledDates={(date) => date > new Date()}
                    />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1.5">End date</p>
                    <DatePicker
                      date={customEndDate}
                      onSelect={setCustomEndDate}
                      placeholder="End"
                      className="w-full h-9"
                      showClearButton={true}
                      disabledDates={(date) => date > new Date() || (customStartDate ? date < customStartDate : false)}
                    />
                  </div>
                </div>
                {customRangeExceedsMax && (
                  <p className="text-xs text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] rounded-md px-3 py-2 mt-2">
                    Date range is too large ({customRangeDays} days). Maximum allowed is {MAX_CUSTOM_RANGE_DAYS} days.
                  </p>
                )}
                {customMatchesMonth && !customRangeExceedsMax && (
                  <p className="text-xs text-[#A16207] bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2 mt-2">
                    Your date range matches {customMatchesMonth}. If a monthly report already exists for this period, it will be updated with the latest data.
                  </p>
                )}
              </>
            )}
          </div>
          {!engineOption && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="include-external"
              checked={includeExternalCampaigns}
              onCheckedChange={(checked) => setIncludeExternalCampaigns(checked === true)}
              className="data-[state=checked]:bg-[#290BD9] data-[state=checked]:border-[#290BD9]"
            />
            <label htmlFor="include-external" className="text-sm text-gray-700 cursor-pointer">
              Include external campaigns not managed by Glitch
            </label>
          </div>
          )}
          {existingReportForPeriod && (
            <div className="flex items-start gap-2 text-xs text-[#290BD9] bg-[#EDE9FE] border border-[#C4B5FD] rounded-md px-3 py-2">
              <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>A {getReportPeriodPill(existingReportForPeriod)?.toLowerCase() || ''} report for this period already exists. Generating will refresh it with the latest data.</span>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsPerformanceModalOpen(false);
                setEngineOption(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerateWeekly}
              disabled={
                generateWeeklyReportMutation.isPending ||
                generateEngineReportMutation.isPending ||
                !organizationData?.id ||
                !currentGoogleAdsAccount ||
                (periodType === 'weekly' && selectedWeekIndex === null) ||
                (periodType === 'monthly' && selectedMonthIndex === null) ||
                (periodType === 'custom' && (!customStartDate || !customEndDate || customRangeExceedsMax))
              }
              className="bg-[#290BD9] hover:bg-[#2309C5] text-white"
            >
              {(generateWeeklyReportMutation.isPending || generateEngineReportMutation.isPending) && <Spinner size="sm" className="mr-2" />}
              {existingReportForPeriod ? 'Refresh Report' : 'Generate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
